import React from 'react';
import { SkillTag, skillTagLabel } from '../../../../services/skillFrameworkService';
import { CourseStagePanel } from '../../shared/CourseStagePanel';
import { useCourseContext } from '../../CourseContext';
import type { CourseDocument } from '../../../../services/courseRunsService';

export function CourseSyllabus() {
  const { document } = useCourseContext();
  const hasTargeting = !!document?.targeting?.title;

  return (
    <div className="p-6 max-w-4xl">
      <CourseStagePanel
        stageId="course_syllabus"
        title="Syllabus Generation"
        description="Generate a structured syllabus based on your course targeting. Uses extended thinking for deeper reasoning."
        isLlmStage
        runDisabled={!hasTargeting}
        runDisabledReason={!hasTargeting ? 'Complete the Setup stage first to provide course targeting.' : undefined}
        renderOutput={(doc) => <SyllabusOutput document={doc} />}
      />
    </div>
  );
}

// Use any for the extended syllabus fields that come from the LLM
// and aren't in our strict CourseDocument type yet
function SyllabusOutput({ document }: { document: CourseDocument }) {
  const syllabus: any = document.syllabus;
  const validation: any = (document as any).syllabus_validation;

  if (!syllabus) {
    return <div className="text-sm text-gray-500">No syllabus generated yet.</div>;
  }

  return (
    <div className="space-y-6">
      <ValidationPanel validation={validation} />

      <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
        <h3 className="text-sm font-semibold text-indigo-900 mb-1">Narrative Arc</h3>
        <p className="text-sm text-indigo-800">{syllabus.narrative_arc}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="text-sm font-semibold text-green-900 mb-2">What You'll Learn</h3>
          <ul className="space-y-1">
            {(syllabus.what_you_will_learn || []).map((item: string, i: number) => (
              <li key={i} className="text-sm text-green-800 flex gap-2">
                <span className="text-green-500 flex-shrink-0">-</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Structured Goals</h3>
          <ul className="space-y-1">
            {(syllabus.structured_goals || []).map((goal: string, i: number) => (
              <li key={i} className="text-sm text-blue-800 flex gap-2">
                <span className="text-blue-500 flex-shrink-0">{i + 1}.</span>
                {goal}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {syllabus.skill_focus && syllabus.skill_focus.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 text-xs">
          <span className="font-medium text-gray-600">Skill Focus:</span>
          {syllabus.skill_focus.map((f: { category?: string; skill?: string; custom?: boolean }, i: number) => (
            <span
              key={i}
              className={`px-2 py-0.5 rounded-full border ${f.custom ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}
            >
              {f.category ? `${f.category} / ${f.skill}` : f.skill}
            </span>
          ))}
        </div>
      )}

      {syllabus.course_glossary && syllabus.course_glossary.length > 0 && (
        <details className="border border-gray-200 rounded-lg">
          <summary className="px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50">
            {'Course Glossary (' + syllabus.course_glossary.length + ' terms)'}
          </summary>
          <div className="px-4 py-2 border-t space-y-1">
            {syllabus.course_glossary.map((entry: any, i: number) => (
              <div key={i} className="text-xs">
                <span className="font-mono font-medium text-gray-900">{entry.term}</span>
                <span className="text-gray-400 ml-1">{'(introduced in lesson ' + entry.introduced_in_lesson + ')'}</span>
                <span className="text-gray-600 ml-2">{entry.canonical_short_definition}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          {'Lessons (' + syllabus.lessons.length + ')'}
        </h3>
        <div className="space-y-3">
          {(syllabus.lessons || []).map((lesson: any) => (
            <LessonCard key={lesson.lesson_number} lesson={lesson} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ValidationPanel({ validation }: { validation: any }) {
  if (!validation || !validation.issues || validation.issues.length === 0) return null;

  const hasErrors = validation.error_count > 0;

  return (
    <div className={`p-4 rounded-lg border ${hasErrors ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <h3 className={`text-sm font-semibold mb-2 ${hasErrors ? 'text-red-900' : 'text-amber-900'}`}>
        {'Validation Issues (' + validation.error_count + ' errors, ' + validation.warning_count + ' warnings)'}
      </h3>
      <ul className="space-y-1">
        {validation.issues.map((issue: any, i: number) => (
          <li key={i} className="text-xs flex gap-2">
            <span className={`flex-shrink-0 font-medium ${issue.severity === 'error' ? 'text-red-600' : 'text-amber-600'}`}>
              {issue.severity === 'error' ? 'ERROR' : 'WARN'}
            </span>
            <span className={issue.severity === 'error' ? 'text-red-800' : 'text-amber-800'}>
              {issue.message}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-500 mt-2">
        Use the Rerun button above to regenerate, or edit the document directly.
      </p>
    </div>
  );
}

function LessonCard({ lesson }: { lesson: any }) {
  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {lesson.lesson_number + '. ' + lesson.title}
            {lesson.lesson_type && lesson.lesson_type !== 'instructional' && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                {lesson.lesson_type}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">{lesson.subtitle}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-xs text-gray-400">{'~' + lesson.estimated_duration_minutes + ' min'}</span>
          {lesson.time_budget && (
            <div className="text-xs text-gray-300 mt-0.5">
              {lesson.time_budget.reading_minutes + 'm read + ' + lesson.time_budget.exercises_minutes + 'm exercises (' + lesson.time_budget.exercise_count_target + ' ex)'}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="font-medium text-gray-600">Objectives:</span>
          <ul className="mt-1 space-y-0.5">
            {(lesson.objectives || []).map((obj: string, i: number) => (
              <li key={i} className="text-gray-700">{'- ' + obj}</li>
            ))}
          </ul>
        </div>
        <div>
          <span className="font-medium text-gray-600">Component Hints:</span>
          <ul className="mt-1 space-y-0.5">
            {(lesson.component_hints || []).map((hint: string, i: number) => (
              <li key={i} className="text-gray-500 italic">{'- ' + hint}</li>
            ))}
          </ul>
        </div>
      </div>

      {lesson.skill_tags && lesson.skill_tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {lesson.skill_tags.map((tag: SkillTag, i: number) => (
            <span key={i} className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
              {skillTagLabel(tag)}
            </span>
          ))}
        </div>
      )}

      <div className="mt-2 text-xs">
        <span className="font-medium text-gray-600">Prerequisites: </span>
        <span className="text-gray-500">{lesson.prerequisites}</span>
      </div>
      <div className="text-xs">
        <span className="font-medium text-gray-600">Exit Knowledge: </span>
        <span className="text-gray-500">{lesson.exit_knowledge}</span>
      </div>
      <div className="mt-1 text-xs text-gray-400 italic">
        {lesson.targeting_context}
      </div>
    </div>
  );
}
